import { Button, Tooltip } from 'antd';
import clsx from 'clsx';
import type { MouseEvent } from 'react';
import React from 'react';
import { type Key, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FileLimitationEnum } from '../../../enums/FileLimitationEnum';
import { FileMenuActionEnum } from '../../../enums/FileMenuActionEnum';
import { useAuth } from '../../../hooks/useAuth';
import { useTags } from '../../../hooks/useTags';
import type File from '../../../interfaces/File';
import type FileAction from '../../../interfaces/FileAction';
import { hasFileLimitation, isFileFavoritesActionsAvailable } from '../../../utils/utils';
import { Icon } from '../../Core/Icon';

export type FilesTableActionsCellVariant = 'title' | 'trailing';

export interface FilesTableActionsCellProps {
  file: File;
  filesActions: FileAction[];
  handleFileMenuAction: (filesIds: number[], action: FileMenuActionEnum) => void;
  handleRowContextMenu: (file: File, e: MouseEvent) => void;
  selectedTableKeys: Key[];
  setSelectedTableKeys: (keys: Key[]) => void;
  variant?: FilesTableActionsCellVariant;
}

const FilesTableActionsCell: React.FC<FilesTableActionsCellProps> = ({
  file,
  handleFileMenuAction,
  handleRowContextMenu,
  setSelectedTableKeys,
  variant = 'trailing',
}) => {
  const { t } = useTranslation();
  const { tags } = useTags();

  const { isInFileRoute, authenticatedUser, isAdminOrContributor } = useAuth();

  /**
   * Should the Share button be shown?
   */
  const isShareVisible = useMemo(() => {
    return !hasFileLimitation(file.fileLimitations, FileLimitationEnum.DenyFileShare);
  }, [file, isInFileRoute, authenticatedUser, isAdminOrContributor]);

  /**
   * Should the Tags button be shown? (must not be denied, and tags must exist)
   */
  const isTagsVisible = useMemo(() => {
    return (
      !hasFileLimitation(file.fileLimitations, FileLimitationEnum.DenyFileTags) && tags.length > 0
    );
  }, [file, isInFileRoute, tags]);

  const isFavoritesVisible = isFileFavoritesActionsAvailable(file, isInFileRoute);

  // --- Render ---
  return (
    <>
      <div
        className={clsx(
          'actions-cell-content z-20 flex h-fit flex-row items-center justify-center',
          variant === 'title'
            ? 'actions-cell-content--title relative shrink-0 px-1 md:hidden'
            : 'border-gray-medium bg-files-highlight to-files-highlight absolute -top-1.5 right-0 border-l bg-linear-to-r from-transparent px-2',
        )}
      >
        {/* Share / Star / Tags — hover-only on mouse; hidden on touch */}
        <div className="actions-cell-quick flex flex-row items-center">
          {isShareVisible && (
            <Button
              size="small"
              className="mr-1"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleFileMenuAction([file.id], FileMenuActionEnum.Share);
              }}
            >
              {t('common.share')}
            </Button>
          )}

          {isFavoritesVisible && (
            <>
              {file.isFavourite ? (
                <Tooltip title={t('a11y.removeFromFavorites')}>
                  <Button
                    type="text"
                    size="small"
                    aria-label={t('a11y.removeFromFavorites')}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleFileMenuAction([file.id], FileMenuActionEnum.RemoveFromFavorites);
                    }}
                  >
                    <Icon name="StarOffIcon" className="w-5" />
                  </Button>
                </Tooltip>
              ) : (
                <Tooltip title={t('a11y.addToFavorites')}>
                  <Button
                    type="text"
                    size="small"
                    aria-label={t('a11y.addToFavorites')}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleFileMenuAction([file.id], FileMenuActionEnum.AddToFavorites);
                    }}
                  >
                    <Icon name="StarIcon" className="w-5" />
                  </Button>
                </Tooltip>
              )}
            </>
          )}

          {isTagsVisible && (
            <Button
              type="text"
              size="small"
              aria-label={t('a11y.editFileTags')}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setSelectedTableKeys([file.id]);
                handleFileMenuAction([file.id], FileMenuActionEnum.Tags);
              }}
            >
              <Icon name="BookmarkIcon" className="h-4 w-4" />
            </Button>
          )}
        </div>

        {/* Row menu / more — always visible on touch and title-inline layouts */}
        <Button
          type="text"
          size="small"
          className="actions-cell-more"
          aria-label={t('a11y.openFileMenu')}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleRowContextMenu(file, e);
          }}
        >
          <Icon name="EllipsisVerticalIcon" className="w-5" />
        </Button>
      </div>
    </>
  );
};

export default FilesTableActionsCell;
